import i18next from "i18next"

const IMAGE_LOAD_TIMEOUT_MS = 15000
const DIRECT_IMAGE_EXTENSION = /\.(jpe?g|png|webp)$/i
const IMAGE_FORMAT_QUERY =
  /(?:^|[?&])(?:format|fm|ext)=(jpe?g|png|webp)(?:&|$)/i

const INVALID_IMAGE_URL_MESSAGE =
  "Please enter a valid image URL (JPEG, PNG, or WebP). The link must open an image, not a webpage or document."

const isAcceptableHttpUrl = value => {
  const trimmed = String(value || "").trim()
  if (!/^https?:\/\//i.test(trimmed) || /\s/.test(trimmed)) {
    return false
  }
  try {
    const parsed = new URL(trimmed)
    return (
      (parsed.protocol === "http:" || parsed.protocol === "https:") &&
      Boolean(parsed.hostname)
    )
  } catch {
    return false
  }
}

/** True when the URL path or query already identifies a JPEG, PNG, or WebP file. */
export const looksLikeDirectImageUrl = url => {
  try {
    const parsed = new URL(String(url || "").trim())
    return (
      DIRECT_IMAGE_EXTENSION.test(parsed.pathname) ||
      IMAGE_FORMAT_QUERY.test(parsed.search)
    )
  } catch {
    return false
  }
}

/**
 * Immediate image-URL error. Empty values are not an error here.
 * Webpage, document, and non-http links fail without waiting for a network load,
 * so forms do not keep showing "Field is required" while an image check runs.
 */
export const getSyncInvalidImageUrlMessage = url => {
  const trimmed = String(url || "").trim()
  if (!trimmed) return ""
  if (!isAcceptableHttpUrl(trimmed) || !looksLikeDirectImageUrl(trimmed)) {
    return i18next.t(INVALID_IMAGE_URL_MESSAGE)
  }
  return ""
}

export const loadImageDimensions = url =>
  new Promise((resolve, reject) => {
    const img = new Image()
    let settled = false

    const finish = (fn, value) => {
      if (settled) return
      settled = true
      clearTimeout(timeoutId)
      img.onload = null
      img.onerror = null
      fn(value)
    }

    const resolveDimensions = () => {
      const width = img.naturalWidth || img.width
      const height = img.naturalHeight || img.height
      if (width > 0 && height > 0) {
        finish(resolve, { width, height })
        return true
      }
      return false
    }

    const timeoutId = setTimeout(
      () => finish(reject, new Error("timeout")),
      IMAGE_LOAD_TIMEOUT_MS
    )

    img.onload = () => {
      if (!resolveDimensions()) {
        finish(reject, new Error("load_failed"))
      }
    }
    img.onerror = () => finish(reject, new Error("load_failed"))
    // Do not set crossOrigin — the preview <img> loads the same way and we only
    // need naturalWidth/naturalHeight, not canvas access.
    img.src = url

    // Cached images (e.g. already shown in preview) may load synchronously.
    if (img.complete) {
      if (!resolveDimensions()) {
        finish(reject, new Error("load_failed"))
      }
    }
  })

/**
 * Load a remote URL and verify it is a real image (browser Image decode).
 * Optionally enforce exact size and/or square aspect.
 */
export const validateRemoteImageUrl = async (
  url,
  {
    exactWidth,
    exactHeight,
    requireSquare = false,
    recommendedWidth,
    recommendedHeight,
  } = {}
) => {
  const trimmed = String(url || "").trim()
  if (!trimmed) {
    return { valid: true }
  }

  let width
  let height
  try {
    ;({ width, height } = await loadImageDimensions(trimmed))
  } catch {
    return {
      valid: false,
      code: "load_failed",
      expectedWidth: exactWidth,
      expectedHeight: exactHeight,
      recommendedWidth,
      recommendedHeight,
    }
  }

  if (
    exactWidth != null &&
    exactHeight != null &&
    (width !== exactWidth || height !== exactHeight)
  ) {
    return {
      valid: false,
      code: "wrong_dimensions",
      width,
      height,
      expectedWidth: exactWidth,
      expectedHeight: exactHeight,
    }
  }

  if (requireSquare && width !== height) {
    return {
      valid: false,
      code: "not_square",
      width,
      height,
      recommendedWidth: recommendedWidth ?? exactWidth,
      recommendedHeight: recommendedHeight ?? exactHeight,
    }
  }

  return {
    valid: true,
    width,
    height,
    expectedWidth: exactWidth,
    expectedHeight: exactHeight,
    recommendedWidth,
    recommendedHeight,
  }
}

/** Returns a translated error string, or "" when the URL is acceptable. */
export const getRemoteImageUrlErrorMessage = async (url, options = {}) => {
  const trimmed = String(url || "").trim()
  if (!trimmed) return ""

  const syncMessage = getSyncInvalidImageUrlMessage(trimmed)
  if (syncMessage) return syncMessage

  const result = await validateRemoteImageUrl(trimmed, options)
  if (result.valid) return ""

  if (result.code === "wrong_dimensions") {
    return i18next.t(
      "Image dimensions must be {{itemWidth}}x{{itemHeight}} pixels.",
      {
        itemWidth: options.exactWidth ?? result.expectedWidth,
        itemHeight: options.exactHeight ?? result.expectedHeight,
      }
    )
  }

  if (result.code === "not_square") {
    return i18next.t(
      "Please upload a square image with recommended dimensions of {{width}} × {{height}} px.",
      {
        width:
          options.recommendedWidth ??
          options.exactWidth ??
          result.recommendedWidth ??
          "",
        height:
          options.recommendedHeight ??
          options.exactHeight ??
          result.recommendedHeight ??
          "",
      }
    )
  }

  return i18next.t(INVALID_IMAGE_URL_MESSAGE)
}

export const validateExactImageUrlDimensions = async (
  url,
  { width: expectedWidth, height: expectedHeight } = {}
) =>
  validateRemoteImageUrl(url, {
    exactWidth: expectedWidth,
    exactHeight: expectedHeight,
  })

export const validateSquareImageUrl = async (
  url,
  { recommendedWidth, recommendedHeight } = {}
) =>
  validateRemoteImageUrl(url, {
    requireSquare: true,
    recommendedWidth,
    recommendedHeight,
  })
